import React, { useState, useEffect } from 'react';
import { tournamentsApi, adminApi } from '../services/api';
import { Tournament, DeckSubmission, DeckSubmissionReview } from '../types';

const ReviewSubmissionsPage: React.FC = () => {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [submissions, setSubmissions] = useState<DeckSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    loadTournaments();
  }, []);

  const loadTournaments = async () => {
    try {
      setLoading(true);
      const response = await tournamentsApi.getAll();
      setTournaments(response.data);
    } catch (error) {
      console.error('Failed to load tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async (tournamentId: string, status?: string) => {
    try {
      setSubmissionsLoading(true);
      const response = await adminApi.getTournamentSubmissions(tournamentId, status);
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to load submissions:', error);
    } finally {
      setSubmissionsLoading(false);
    }
  };

  const handleReviewSubmission = async (submissionId: string, review: DeckSubmissionReview) => {
    try {
      await adminApi.reviewSubmission(submissionId, review);
      // Reload submissions to reflect the change
      if (selectedTournament) {
        loadSubmissions(selectedTournament.id, statusFilter);
      }
      alert(`Submission ${review.status.toLowerCase()} successfully!`);
    } catch (error) {
      console.error('Failed to review submission:', error);
      alert('Failed to review submission. Please try again.');
    }
  };

  const handleTournamentSelect = (tournament: Tournament) => {
    setSelectedTournament(tournament);
    loadSubmissions(tournament.id, statusFilter);
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    if (selectedTournament) {
      loadSubmissions(selectedTournament.id, status);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-600">Loading tournaments...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Review Submissions</h1>

        {!selectedTournament ? (
          <TournamentSelection
            tournaments={tournaments}
            onSelectTournament={handleTournamentSelect}
          />
        ) : (
          <SubmissionReview
            tournament={selectedTournament}
            submissions={submissions}
            submissionsLoading={submissionsLoading}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
            onReviewSubmission={handleReviewSubmission}
            onBack={() => setSelectedTournament(null)}
          />
        )}
      </div>
    </div>
  );
};

interface TournamentSelectionProps {
  tournaments: Tournament[];
  onSelectTournament: (tournament: Tournament) => void;
}

const TournamentSelection: React.FC<TournamentSelectionProps> = ({ tournaments, onSelectTournament }) => {
  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Select a Tournament</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tournaments.map((tournament) => (
          <div
            key={tournament.id}
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => onSelectTournament(tournament)}
          >
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{tournament.name}</h3>
            <p className="text-gray-600 mb-2">{tournament.description}</p>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Date:</span>
                <span className="text-sm font-medium">{new Date(tournament.date).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Series:</span>
                <span className="text-sm font-medium">{tournament.circuit?.name || 'Unknown'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Budget:</span>
                <span className="text-sm font-medium">€{tournament.ruleset?.budget_limit || 'Unknown'}</span>
              </div>
            </div>
            <div className="mt-4">
              <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                Review Submissions
              </button>
            </div>
          </div>
        ))}
      </div>

      {tournaments.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">No tournaments found.</p>
        </div>
      )}
    </div>
  );
};

interface SubmissionReviewProps {
  tournament: Tournament;
  submissions: DeckSubmission[];
  submissionsLoading: boolean;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  onReviewSubmission: (submissionId: string, review: DeckSubmissionReview) => void;
  onBack: () => void;
}

const SubmissionReview: React.FC<SubmissionReviewProps> = ({
  tournament,
  submissions,
  submissionsLoading,
  statusFilter,
  onStatusFilterChange,
  onReviewSubmission,
  onBack,
}) => {
  const [selectedSubmission, setSelectedSubmission] = useState<DeckSubmission | null>(null);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      case 'SUBMITTED':
        return 'bg-yellow-100 text-yellow-800';
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{tournament.name}</h2>
          <p className="text-gray-600">Review deck submissions</p>
        </div>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
        >
          Back to Tournaments
        </button>
      </div>

      <div className="mb-6 flex space-x-4">
        <select
          value={statusFilter}
          onChange={(e) => onStatusFilterChange(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="SUBMITTED">Submitted</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>
      </div>

      {submissionsLoading ? (
        <div className="flex justify-center items-center h-32">
          <div className="text-lg text-gray-600">Loading submissions...</div>
        </div>
      ) : (
        <div className="space-y-4">
          {submissions.map((submission) => (
            <div key={submission.id} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">{submission.deck_name}</h3>
                  <p className="text-gray-600 mb-1">
                    Submitted: {new Date(submission.created_at).toLocaleString()}
                  </p>
                  <p className="text-gray-600">
                    Total Cost: €{submission.total_cost.toFixed(2)}
                  </p>
                </div>
                <div className="text-right">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(submission.status)}`}>
                    {submission.status}
                  </span>
                </div>
              </div>

              {submission.validation_errors && submission.validation_errors.length > 0 && (
                <div className="mb-4 p-3 bg-red-50 rounded-lg">
                  <h4 className="font-medium text-red-800 mb-2">Validation Errors:</h4>
                  <ul className="text-sm text-red-700 space-y-1">
                    {submission.validation_errors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex space-x-2">
                <button
                  onClick={() => setSelectedSubmission(submission)}
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors text-sm"
                >
                  View Details
                </button>
                {submission.status === 'SUBMITTED' && (
                  <>
                    <button
                      onClick={() => onReviewSubmission(submission.id, { status: 'APPROVED' })}
                      className="px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors text-sm"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        const reason = prompt('Enter rejection reason (optional):');
                        onReviewSubmission(submission.id, { 
                          status: 'REJECTED', 
                          rejection_reason: reason || undefined 
                        });
                      }}
                      className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors text-sm"
                    >
                      Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}

          {submissions.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No submissions found for this tournament.</p>
            </div>
          )}
        </div>
      )}

      {selectedSubmission && (
        <SubmissionDetails
          submission={selectedSubmission}
          onClose={() => setSelectedSubmission(null)}
        />
      )}
    </div>
  );
};

interface SubmissionDetailsProps {
  submission: DeckSubmission;
  onClose: () => void;
}

const SubmissionDetails: React.FC<SubmissionDetailsProps> = ({ submission, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-800">{submission.deck_name}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <p className="text-gray-900">{submission.status}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Cost</label>
              <p className="text-gray-900">€{submission.total_cost.toFixed(2)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Submitted</label>
              <p className="text-gray-900">{new Date(submission.created_at).toLocaleString()}</p>
            </div>
            {submission.approved_at && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Reviewed</label>
                <p className="text-gray-900">{new Date(submission.approved_at).toLocaleString()}</p>
              </div>
            )}
          </div>

          {submission.rejection_reason && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Rejection Reason</label>
              <p className="text-red-600">{submission.rejection_reason}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Decklist</label>
            <pre className="bg-gray-50 p-4 rounded-lg text-sm overflow-x-auto">
              {submission.decklist_text}
            </pre>
          </div>

          {submission.validation_errors && submission.validation_errors.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Validation Errors</label>
              <ul className="text-sm text-red-700 space-y-1">
                {submission.validation_errors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewSubmissionsPage;
